export type PartyKind = "individual" | "business";

export type OrderCustomerOption = {
  partyOrganizationId: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerGst: string;
  customerAddress: string;
  kind: PartyKind;
  partyType: string;
  source: "party" | "order_book";
};

type AssociationManufacturer = {
  _id?: string;
  id?: string;
  name?: string;
  phone?: string;
  email?: string;
  GST?: string;
  gst?: string;
  address?: unknown;
  spocName?: string;
};

type AssociationRow = {
  manufacturer?: AssociationManufacturer[] | null;
  partyType?: string;
};

export const partyKindFromGst = (gst?: string | null): PartyKind => {
  const value = String(gst || "").trim();
  if (!value || /^n\/?a$/i.test(value)) {
    return "individual";
  }
  return "business";
};

export const visibleGst = (gst?: string | null) => {
  const value = String(gst || "").trim();
  if (!value || /^n\/?a$/i.test(value)) {
    return "";
  }
  return value.toUpperCase();
};

export const formatPartyAddress = (address?: unknown) => {
  if (!address) {
    return "";
  }
  if (typeof address === "string") {
    return address.trim();
  }
  if (typeof address !== "object") {
    return "";
  }
  const row = address as {
    addressLane?: string;
    city?: string;
    state?: string;
    pinCode?: string | number;
  };
  return [row.addressLane, row.city, row.state, row.pinCode]
    .map((part) => String(part ?? "").trim())
    .filter((part) => part && part !== "0")
    .join(", ");
};

const firstManufacturer = (row?: AssociationRow | null): AssociationManufacturer | null => {
  const list = row?.manufacturer;
  if (!Array.isArray(list) || list.length === 0 || !list[0]) {
    return null;
  }
  return list[0];
};

export const mapAssociationToCustomer = (row?: AssociationRow | null): OrderCustomerOption | null => {
  const org = firstManufacturer(row);
  if (!org) {
    return null;
  }
  const partyOrganizationId = String(org._id || org.id || "").trim();
  const customerName = String(org.name || "").trim();
  if (!partyOrganizationId || !customerName) {
    return null;
  }
  return {
    partyOrganizationId,
    customerName,
    customerPhone: String(org.phone || "").trim(),
    customerEmail: String(org.email || "").trim(),
    customerGst: visibleGst(org.GST || org.gst),
    customerAddress: formatPartyAddress(org.address),
    kind: partyKindFromGst(org.GST || org.gst),
    partyType: String(row?.partyType || "").trim() || "Customer",
    source: "party",
  };
};

export const mapSearchRowToCustomer = (row?: Record<string, unknown> | null): OrderCustomerOption | null => {
  const customerName = String(row?.customerName || "").trim();
  if (!customerName) {
    return null;
  }
  const kindRaw = String(row?.kind || "");
  return {
    partyOrganizationId: String(row?.partyOrganizationId || "").trim(),
    customerName,
    customerPhone: String(row?.customerPhone || "").trim(),
    customerEmail: String(row?.customerEmail || "").trim(),
    customerGst: visibleGst(String(row?.GST || row?.gst || "")),
    customerAddress: formatPartyAddress(row?.customerAddress || row?.address),
    kind: kindRaw === "business" ? "business" : partyKindFromGst(String(row?.GST || row?.gst || "")),
    partyType: String(row?.partyType || "Customer"),
    source: row?.source === "order_book" ? "order_book" : "party",
  };
};

export const shouldOfferCreateParty = (
  query: string,
  matches: Array<OrderCustomerOption | null>
): boolean => {
  const name = String(query || "").trim().toLowerCase();
  if (!name) {
    return false;
  }
  return !matches.some(
    (item) => item && String(item.customerName || "").trim().toLowerCase() === name
  );
};

export const isOrderAudioNote = (contentType?: string | null, fileName?: string | null): boolean => {
  const mime = String(contentType || "").trim().toLowerCase();
  if (mime.startsWith("audio/")) {
    return true;
  }
  const name = String(fileName || "").trim().toLowerCase();
  return /\.(webm|mp3|m4a|mp4|ogg|wav|aac)$/i.test(name);
};

export const pickAudioRecorderMime = (): string => {
  const types = ["audio/webm", "audio/mp4", "audio/mpeg", "audio/ogg"];
  if (typeof MediaRecorder === "undefined") {
    return "";
  }
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
};
